import { useEffect, useCallback, useState } from 'react';
import { connect } from 'react-redux';

import { SET_SCHEDULE_PUBLISHED_CONFIRMATION_SHOWING} from '../actions/types';
import { siteboardAction } from '../actions/defaultActions';

const SchedulePublishedConfirmationDialogue = ({ siteboardAction }) => {
  const [isClosing, setIsClosing] = useState(false);

  const closeDialogue = useCallback(() => {
    siteboardAction(SET_SCHEDULE_PUBLISHED_CONFIRMATION_SHOWING, {
      isOpen: false,
    });
  }, [siteboardAction]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsClosing(true);
    }, 5000);

    return () => clearTimeout(timeout);
  }, []);

  const handleExitAnimationEnd = (event) => {
    // Close only after the overlay's exit animation finishes.
    if (
      event.target === event.currentTarget &&
      event.animationName === 'schedule-published-overlay-exit'
    ) {
      closeDialogue();
    }
  };

  return (
    <>
      <style>{`
        .schedule-published-overlay {
          --schedule-published-exit-duration: 300ms;
        }

        .schedule-published-overlay.is-closing {
          animation: schedule-published-overlay-exit
            var(--schedule-published-exit-duration) ease-in both;
        }

        .schedule-published-overlay.is-closing .schedule-published-content {
          animation: schedule-published-content-exit
            var(--schedule-published-exit-duration)
            cubic-bezier(0.4, 0, 1, 1) both;
        }

        .schedule-published-pop {
          animation: schedule-published-pop 450ms
            cubic-bezier(0.22, 1, 0.36, 1) both;
        }

        .schedule-published-check {
          stroke-dasharray: 1;
          stroke-dashoffset: 1;
          animation: schedule-published-check 350ms 300ms ease-out both;
        }

        .schedule-published-ring {
          animation: schedule-published-ring 700ms 350ms ease-out both;
        }

        .schedule-published-spark {
          animation: schedule-published-spark 700ms 350ms ease-out both;
        }

        .schedule-published-copy {
          animation: schedule-published-copy 350ms 250ms ease-out both;
        }

        .schedule-published-background {
          transform-origin: center;
          animation: schedule-published-background-expand 550ms
            cubic-bezier(0.16, 1, 0.3, 1) both;
        }

        @keyframes schedule-published-overlay-exit {
          from { opacity: 1; }
          to { opacity: 0; }
        }

        @keyframes schedule-published-content-exit {
          from { transform: scale(1); opacity: 1; }
          to { transform: scale(0.65); opacity: 0; }
        }

        @keyframes schedule-published-pop {
          0% { opacity: 0; transform: scale(0.65) rotate(-8deg); }
          65% { opacity: 1; transform: scale(1.08) rotate(2deg); }
          100% { opacity: 1; transform: scale(1) rotate(0deg); }
        }

        @keyframes schedule-published-check {
          from { stroke-dashoffset: 1; }
          to { stroke-dashoffset: 0; }
        }

        @keyframes schedule-published-ring {
          from { opacity: 0.7; transform: scale(0.9); }
          to { opacity: 0; transform: scale(1.9); }
        }

        @keyframes schedule-published-spark {
          0% {
            opacity: 0;
            transform: translate(-50%, -50%) translateY(-25px) scale(0);
          }
          25% { opacity: 1; }
          100% {
            opacity: 0;
            transform: translate(-50%, -50%) translateY(-60px) scale(0.5);
          }
        }

        @keyframes schedule-published-copy {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes schedule-published-background-expand {
          from { transform: scale(0.12); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }

        @media (prefers-reduced-motion: reduce) {
          .schedule-published-overlay {
            --schedule-published-exit-duration: 1ms;
          }

          .schedule-published-overlay.is-closing .schedule-published-content,
          .schedule-published-pop,
          .schedule-published-copy,
          .schedule-published-background {
            animation: none;
          }

          .schedule-published-check {
            animation: none;
            stroke-dashoffset: 0;
          }

          .schedule-published-ring,
          .schedule-published-spark {
            display: none;
          }
        }
      `}</style>

      <div
        className={`schedule-published-overlay fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 ${
          isClosing ? 'is-closing' : ''
        }`}
        role='status'
        aria-live='polite'
        aria-atomic='true'
        onAnimationEnd={handleExitAnimationEnd}
      >
        <div className='schedule-published-content relative isolate flex aspect-square w-[24rem] max-w-full flex-col items-center justify-center gap-5 p-8 sm:p-8'>
          {/* Expanding circular background */}
          <div
            className='schedule-published-background pointer-events-none absolute inset-0 -z-10 rounded-full bg-neutral-900 shadow-2xl'
            aria-hidden='true'
          />

          <div
            className='schedule-published-pop relative flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-emerald-400/10'
            aria-hidden='true'
          >
            <div className='schedule-published-ring absolute inset-0 rounded-full border-2 border-emerald-400/60' />

            {Array.from({ length: 8 }, (_, index) => (
              <div
                key={index}
                className='pointer-events-none absolute inset-0'
                style={{ transform: `rotate(${index * 45}deg)` }}
              >
                <span
                  className='schedule-published-spark absolute left-1/2 top-1/2 h-1.5 w-1.5 rounded-full'
                  style={{
                    backgroundColor: index % 2 ? '#f9a928' : '#34d399',
                  }}
                />
              </div>
            ))}

            {/* Calendar with a checkmark drawn after it appears */}
            <svg
              width='42'
              height='42'
              viewBox='0 0 40 40'
              fill='none'
              className='relative overflow-visible text-emerald-400'
            >
              <rect
                x='5'
                y='8'
                width='30'
                height='28'
                rx='5'
                className='fill-neutral-900'
                stroke='currentColor'
                strokeWidth='2.5'
              />
              <path
                d='M12 4v8M28 4v8M5 17h30'
                stroke='currentColor'
                strokeWidth='2.5'
                strokeLinecap='round'
              />
              <path
                className='schedule-published-check'
                pathLength='1'
                d='m13 26 5 5 10-10'
                stroke='currentColor'
                strokeWidth='3'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>

          <div className='schedule-published-copy max-w-[18rem] text-center'>
            <div className='text-[1.2em] font-semibold leading-tight text-white'>
              Schedule published!
            </div>
            <div className='mt-3 leading-relaxed text-white/80'>
              When you’re ready, create next week’s{' '}
              <span className='font-medium text-white whitespace-nowrap'>
                3-week schedule
              </span>{' '}
              to keep things moving.
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default connect(null, { siteboardAction })(
  SchedulePublishedConfirmationDialogue
);
